import { ReactNode } from 'react';
import { Logo } from '@/components/Logo';
import { RightPanel } from '@/pages/auth/login/components/rightPanel';

interface AuthPageLayoutProps {
  description: string;
  children: ReactNode;
}

/**
 * Reusable page layout for authentication flows
 *
 * Provides consistent layout with Logo, description, and animated right panel
 *
 * @example
 * ```tsx
 * export function SelectWorkspacePage() {
 *   const { t } = useTranslation();
 *
 *   return (
 *     <AuthPageLayout description={t('selectWorkspaceDescription')}>
 *       <WorkspaceList />
 *     </AuthPageLayout>
 *   );
 * }
 * ```
 */
export function AuthPageLayout({ description, children }: AuthPageLayoutProps) {
  return (
    <div className="grid min-h-svh lg:grid-cols-[40%_60%]">
      {/* Left Column - Form Content */}
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex items-center justify-center flex-1">
          <div className="w-full max-w-sm">
            {/* Logo Header */}
            <div className="flex flex-col items-center mb-8">
              <Logo className="mb-2" />
              <div className="mt-4 text-sm text-center text-fg-secondary">{description}</div>
            </div>

            {/* Form Content */}
            {children}
          </div>
        </div>
      </div>

      {/* Right Column - Animated Pattern */}
      <RightPanel />
    </div>
  );
}
